import { ReactNode, useState } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import { useControls } from 'react-zoom-pan-pinch'
import {
  Add,
  Contrast,
  Flip,
  InvertColors,
  Remove,
  RestartAlt,
  RotateLeft,
  RotateRight
} from '@mui/icons-material'
import {
  MAX_CONTRAST,
  MIN_CONTRAST,
  controlBarSx,
  controlButtonSx,
  visuallyHiddenSx
} from './constants'
import { ImageAdjustmentsControls } from './useImageAdjustments'
import {
  IMAGE_SHORTCUT_HINTS,
  IShortcutHint,
  ImageShortcutAction,
  getAriaKeyShortcuts,
  getShortcutLabel,
  getShortcutTitle
} from './shortcuts'
import useImageShortcuts from './useImageShortcuts'
import useSteppedZoomControls from './useSteppedZoomControls'
import ShortcutsHelp from './ShortcutsHelp'

interface IAdjustmentButton {
  action: ImageShortcutAction
  onClick: () => void
  disabled?: boolean
  pressed?: boolean
  children: ReactNode
}

const AdjustmentButton: React.FC<IAdjustmentButton> = ({
  action,
  onClick,
  disabled,
  pressed,
  children
}) => (
  <IconButton
    onClick={onClick}
    disabled={disabled}
    size='small'
    aria-label={getShortcutLabel(action)}
    title={getShortcutTitle(action)}
    aria-keyshortcuts={getAriaKeyShortcuts(action)}
    aria-pressed={pressed}
    sx={{
      ...controlButtonSx,
      ...(pressed && { bgcolor: 'rgba(0, 0, 0, 0.15)' })
    }}
  >
    {children}
  </IconButton>
)

interface IAdjustmentControls extends ImageAdjustmentsControls {
  isZoomedIn: boolean
  zoomStep: number
  minZoom: number
  maxZoom: number
  /** Atalhos que a tela em volta já trata, só para aparecer na lista. */
  extraShortcuts?: IShortcutHint[]
}

/**
 * Precisa estar dentro do `TransformWrapper`: "Restaurar ajustes" também volta o zoom a 100%, e os
 * atalhos de teclado (ligados enquanto a barra está montada) incluem o zoom.
 */
const AdjustmentControls: React.FC<IAdjustmentControls> = ({
  adjustments,
  isAdjusted,
  toggleFlip,
  toggleInvert,
  rotateLeft,
  rotateRight,
  increaseContrast,
  decreaseContrast,
  reset,
  isZoomedIn,
  zoomStep,
  minZoom,
  maxZoom,
  extraShortcuts = []
}) => {
  const { resetTransform } = useControls()
  const { zoomIn, zoomOut } = useSteppedZoomControls({
    step: zoomStep,
    minZoom,
    maxZoom
  })
  const [isHelpOpen, setIsHelpOpen] = useState(false)

  const handleRestore = () => {
    reset()
    void resetTransform()
  }

  useImageShortcuts({
    zoomIn,
    zoomOut,
    toggleFlip,
    rotateLeft,
    rotateRight,
    increaseContrast,
    decreaseContrast,
    toggleInvert,
    restore: handleRestore,
    toggleHelp: () => setIsHelpOpen(open => !open)
  })

  return (
    <Box
      role='group'
      aria-label='Ajustes da imagem'
      sx={{
        ...controlBarSx,
        // Em telas estreitas a barra rola na horizontal em vez de esconder os últimos botões.
        maxWidth: '100%',
        overflowX: 'auto',
        scrollbarWidth: 'thin',
        '& > *': { flexShrink: 0 }
      }}
    >
      <AdjustmentButton
        action='toggleFlip'
        onClick={toggleFlip}
        pressed={adjustments.flipped}
      >
        <Flip fontSize='small' />
      </AdjustmentButton>
      <AdjustmentButton action='rotateLeft' onClick={rotateLeft}>
        <RotateLeft fontSize='small' />
      </AdjustmentButton>
      <AdjustmentButton action='rotateRight' onClick={rotateRight}>
        <RotateRight fontSize='small' />
      </AdjustmentButton>
      <AdjustmentButton
        action='decreaseContrast'
        onClick={decreaseContrast}
        disabled={adjustments.contrast <= MIN_CONTRAST}
      >
        <Remove fontSize='small' />
      </AdjustmentButton>
      <Typography
        title='Contraste'
        sx={{
          minWidth: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.25,
          color: 'black',
          fontSize: '0.875rem',
          fontWeight: 500
        }}
      >
        <Contrast sx={{ fontSize: '1rem' }} aria-hidden />
        <Box component='span' sx={visuallyHiddenSx}>
          Contraste
        </Box>
        {Math.round(adjustments.contrast * 100)}%
      </Typography>
      <AdjustmentButton
        action='increaseContrast'
        onClick={increaseContrast}
        disabled={adjustments.contrast >= MAX_CONTRAST}
      >
        <Add fontSize='small' />
      </AdjustmentButton>
      <AdjustmentButton
        action='toggleInvert'
        onClick={toggleInvert}
        pressed={adjustments.inverted}
      >
        <InvertColors fontSize='small' />
      </AdjustmentButton>
      <AdjustmentButton
        action='restore'
        onClick={handleRestore}
        disabled={!isAdjusted && !isZoomedIn}
      >
        <RestartAlt fontSize='small' />
      </AdjustmentButton>
      <ShortcutsHelp
        open={isHelpOpen}
        onOpenChange={setIsHelpOpen}
        shortcuts={[...IMAGE_SHORTCUT_HINTS, ...extraShortcuts]}
      />
    </Box>
  )
}

export default AdjustmentControls
